import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core'
import { FormBuilder, FormGroup, Validators } from "@angular/forms"
import { lastValueFrom, Subject, Subscription } from 'rxjs'
import { CombinerValidator } from "../../form/validator/combiner-validator"
import {
  ConfigurableProduct,
  DimensionSelectionOptionInterface,
  DimensionSelectionsInterface,
  DistinctConfigurableProductInterface,
  getUrlHashArgumentValue,
  PictureInterface,
  ProductDimensionInterface,
  ProductDimensionOptionInterface
} from "@gea-waldviertler-website/gea-models";
import { ProductSelectorComponent } from "../product.selector.component";
import { HttpProductService } from "../../services/http/product.service";
import { CartService } from "../../services/cart.service";
import { EnvService } from "@gea-waldviertler-website/shop-lib";

export interface FormValues {
  [key: string]: string
}

export interface FormFieldsInterface {
  [key: string]: any[]
}

export interface PossibleSelectionsInterface {
  [key: string]: DimensionSelectionOptionInterface[]
}

export interface DistinctConfigOption {
  label: string
  imageSlug?: string
}

@Component({
    selector: 'gea-shop-combiner',
    templateUrl: './combiner.component.html',
    styleUrls: ['./combiner.component.scss'],
    standalone: false
})
export class CombinerComponent extends ProductSelectorComponent implements OnInit, OnChanges, OnDestroy {

  @Input() product?: ConfigurableProduct
  @Input() resetSelectionSubject?: Subject<boolean>
  // Re-emit trigger fired by ProductComponent on a same-product Aktion re-emit.
  @Input() distinctRefreshSubject?: Subject<void>

  @Output("distinctProductSelected") distinctProductSelectedEvent = new EventEmitter<DistinctConfigurableProductInterface | undefined>()
  @Output("priceChanged") priceChangedEvent = new EventEmitter<number>()
  @Output("pictureChanged") pictureChangedEvent = new EventEmitter<PictureInterface[]>()

  configPictures?: PictureInterface[] = []
  dimensionSelections: DimensionSelectionsInterface = {}
  possibleSelections: PossibleSelectionsInterface = {}

  form?: FormGroup

  validating: boolean = false

  basePrice: number = 0
  distinctPrice: number = 0

  // the id of the dimension the triggered an api call
  fetchingDimensionId: number = 0
  errorDimensionId: number = 0

  private envService = inject(EnvService)
  // zoneless: the dimension options arrive via a Promise callback, so the view has to be marked dirty explicitly
  private cd = inject(ChangeDetectorRef)

  private subscriptions: Subscription[] = []

  constructor(
    @Inject(HttpProductService) private httpProductService: HttpProductService,
    @Inject(CartService) private cartService: CartService,
    private fb: FormBuilder
  ) {
    super()
    this.cartService = cartService
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.hasOwnProperty('product')) {
      this.onProductLoad()
    }
  }

  ngOnInit(): void {
    this.onProductLoad()

    // Re-resolve + re-emit the distinct product on demand. On live, the active shop event ("Aktion")
    // arrives asynchronously after the initial render, so ProductService re-emits the product page for
    // the SAME instance. Re-running the resolution keeps an already-resolved configurable product's
    // order tiles + add-to-cart button alive and refreshes the Aktion price/badge.
    const refreshSub = this.distinctRefreshSubject?.subscribe(() => {
      this.handleDistinct()
    })
    if (refreshSub) this.subscriptions.push(refreshSub)
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe())
    this.subscriptions = []
  }

  onProductLoad() {
    this.dimensionSelections = {}
    this.possibleSelections = {}
    this.distinctPrice = 0
    this.basePrice = 0
    
    let formFields: FormFieldsInterface = {}
    if (this.product) {
      this.basePrice = this.product.priceFrom

      let i = 0
      for (const dimension of this.product.selectableDimensions) {
        if (i == 0) {
          this.possibleSelections[dimension.dimensionId] = dimension.options.map(value => { return { optionId: value.optionId } as DimensionSelectionOptionInterface })
        }
        this.dimensionSelections[dimension.dimensionId] = 0
        formFields[dimension.dimensionId] = [0, { validators: [ Validators.required, CombinerValidator.notZero ] }]
        i++
      }
    }

    const resetSub = this.resetSelectionSubject?.subscribe(value => {
      if (value) {
        this.reset()
      }
    })
    if (resetSub) this.subscriptions.push(resetSub)

    // on edit from cart
    const cart = this.cartService.cart()
    const cartItemUuid = getUrlHashArgumentValue('cartItem')
    if (cart && cartItemUuid) {
      const cartItem = this.cartService.get(cartItemUuid)
      if (cartItem) {
        cartItem.getSelectedDimensions().forEach((element, index) => {
          this.selectDimensionOption(element.dimension, element.optionId)
          this.dimensionSelections[element.dimension.dimensionId] = element.optionId
        })
      }
    }

    this.form = this.fb.group(formFields)
  }

  handleDistinct() {
    let ds = this.getDistinctProduct()
    if (ds) {
      this.distinctProductSelectedEvent.emit(ds)
    } else {
      this.distinctProductSelectedEvent.emit(undefined)
    }
  }

  getDistinctProduct(): DistinctConfigurableProductInterface | undefined {
    if(! this.product || ! this.product.variants[0] || ! this.configurationIsFinished()) {
      return undefined
    }

    // set first config image as list image
    if(this.configPictures && this.configPictures.length > 0) {
      this.product.listPicture = this.configPictures[0]
      this.product.variants[0].images = this.configPictures
    }
    
    const variant = this.product.variants[0]
    return {
      dimensionSelections: { ... this.dimensionSelections },
      price: this.distinctPrice,
      qualityId: 1,
      selectedVariant: variant.variantId,
      distinctId: undefined,
      priceSpecial: undefined,
      product: this.product
    }
  }

  configurationIsFinished(): boolean {
    if(! this.product || this.fetchingDimensionId !== 0) {
      return false
    }

    const lastDimension = this.getLastDimension()
    if(! this.product || ! lastDimension) {
      return false
    }

    for (let [dimensionId, optionId] of Object.entries(this.dimensionSelections)) {
      if(parseInt(dimensionId) === lastDimension.dimensionId && optionId != 0) {
        // check if optionId is in possibleSelections
        if (! this.possibleSelections[dimensionId]) return false
        if(this.possibleSelections[dimensionId].map(selection => selection.optionId).includes(optionId)) {
          return true
        }
      }
    }

    return false
  }

  getDistinctOptions(dimension: ProductDimensionInterface): ProductDimensionOptionInterface[] {
    let distinctOptions : ProductDimensionOptionInterface[] = []

    // first add selectable option
    if(this.possibleSelections[dimension.dimensionId]) {
      for(let dimOption of dimension.options) {
        const possibleOptionIdsForDim = Object.values(this.possibleSelections[dimension.dimensionId]).map(selectable => selectable.optionId)
        if(! possibleOptionIdsForDim.includes(dimOption.optionId)) continue

        let exists : boolean = distinctOptions.find(foo => foo.label == dimOption.label) != undefined
        if(exists) continue
  
        // check if option is selectable
        let selectableOption = this.getSelectableOption(dimension, dimOption.optionId)
        if(selectableOption != null) {
          distinctOptions.push(dimOption)
        }
      }
    }
    
    // next fill remaining options (that are not selectable)
    for(let dimOption of dimension.options) {
      let exists : boolean = distinctOptions.find(foo => foo.label == dimOption.label) != undefined

      if(exists == false) {
        distinctOptions.push(dimOption)
      }
    }

    // sort the options, because it will be added kinda random
    return distinctOptions.sort((a, b) => {
      let numA = parseInt(a.value, 10)
      let numB = parseInt(b.value, 10)
      if(!isNaN(numA) || !isNaN(numB)) {
        if (numA > numB) return 1
        if (numA < numB) return -1
      }
      
      if (a.value > b.value) return 1
      if (a.value < b.value) return -1
      if (a.label > b.label) return 1
      if (a.label < b.label) return -1
      return 0;
    })
  }

  getOption(dimension: ProductDimensionInterface, idOption: number): ProductDimensionOptionInterface | null {
    let selectedOption: ProductDimensionOptionInterface | null = null
    for(let option of dimension.options) {
      if(option.optionId == idOption) {
        selectedOption = option
        break
      }
    }

    return selectedOption
  }

  getSelectableOption(dimension: ProductDimensionInterface, idOption: number): ProductDimensionOptionInterface | null {
    let selectedOption: ProductDimensionOptionInterface | null = null

    if(! this.possibleSelections[dimension.dimensionId]) {
      return null
    }

    for(let selection of Object.values(this.possibleSelections[dimension.dimensionId])) {
      for(let option of dimension.options) {
        if(option.optionId == selection.optionId && option.optionId == idOption) {
          selectedOption = option
          return selectedOption
        }
      }
    }

    return selectedOption
  }

  selectDimensionOption(selectedDimension: ProductDimensionInterface, idOption: number) {
    let option = this.getSelectableOption(selectedDimension, idOption)
    if(option == null) {
      return
    }
    
    let optionId = option.optionId
    let dimensionId = selectedDimension.dimensionId

    if (this.dimensionSelections[dimensionId] == optionId) {
      // same value, do nothing
    } else {
      // if it has been changed, set this selection
      this.dimensionSelections[dimensionId] = optionId
      this.loadPossibleSelection(dimensionId)
    }
  }

  loadPossibleSelection(dimensionId?: number) {
    if (! this.product) return

    this.errorDimensionId = 0
    this.fetchingDimensionId = dimensionId ? dimensionId : 0
    const dimensions$ = this.httpProductService.getDimensionOptions(this.product.slug, this.getSelectionsArray(this.dimensionSelections))

    lastValueFrom(dimensions$).then(result => {
      for (const item of result.selectableDimensions) {
        this.possibleSelections[item.dimensionId] = item.options
      }

      this.configPictures = result.productImages
      this.pictureChangedEvent.emit(this.configPictures)
      this.resetSelectedOptions(dimensionId)
    }).catch(() => {
      this.errorDimensionId = this.fetchingDimensionId
      this.fetchingDimensionId = 0
    }).finally(() => {
      this.calculatePrice()
      this.handleDistinct()
      this.cd.markForCheck()
    })
  }

  getLastDimension() {
    if(! this.product) {
      return null
    }

    return this.product.selectableDimensions[this.product.selectableDimensions.length - 1]
  }

  /*
   * Iterate through all Dimensions
   *
   *   - if the selectedOption gets replaced with an new option from possibleSelections
   *     set the new value in this dimension 
   *     loadPossibleSelection again
   * 
   *   - if the selectedOption is not found in the possibleSelections
   *     reset every dimension after this one
   */

  // we also can set an little loading icon instead of "OK" or "ERROR" on the current dimension we are
  resetSelectedOptions(dimensionId?: number) {
    if(! this.product) {
      return
    }

    let clearDimensions = false

    const lastDimension = this.product.selectableDimensions.find(dim => dim.dimensionId == dimensionId)
    for(let dimension of this.product.selectableDimensions) {

      // skip to preset dimension
      if(lastDimension && dimension.order <= lastDimension.order ) {
        continue
      }

      // reset 
      if(clearDimensions) {
        this.dimensionSelections[dimension.dimensionId] = 0
        continue
      }

      this.fetchingDimensionId = dimension.dimensionId
      let selectedOptionId = this.dimensionSelections[dimension.dimensionId]
      
      // if the possibleSelections include the selected option we can quit
      if(this.possibleSelections[dimension.dimensionId].map(selection => selection.optionId).includes(selectedOptionId)) {
        continue
      }

      // set possible option with same label
      for(let possibleOpt of this.possibleSelections[dimension.dimensionId]) {
        let possibleOption = dimension.options.find(opt => opt.optionId == possibleOpt.optionId)
        let selectedOption = dimension.options.find(opt => opt.optionId == selectedOptionId)
        if(possibleOption == undefined || selectedOption == undefined) {
          continue
        }

        // if we found the new selectable option, replace the selected option and load the possible selections again, continue on the next dimension
        if(selectedOption.label == possibleOption.label) {
          this.dimensionSelections[dimension.dimensionId] = possibleOption.optionId
          this.loadPossibleSelection(dimension.dimensionId)
          return
        }
      }

      // no selectable option was found, reset all dimensions after this one, includeing this one
      clearDimensions = true
      this.dimensionSelections[dimension.dimensionId] = 0
    }

    this.fetchingDimensionId = 0
  }

  calculatePrice(dimensionSelections?: DimensionSelectionsInterface): void {
    if (! this.product) {
      this.distinctPrice = 0
      this.basePrice = 0
      return
    }

    this.distinctPrice = this.basePrice
    let thisDimensionSelections = dimensionSelections ?? this.dimensionSelections
    
    for (const dimension of this.product?.selectableDimensions) {
      for (let [dimensionId, optionId] of Object.entries(thisDimensionSelections)) {
        if (dimension.dimensionId == parseInt(dimensionId)) {
          for (const selection of dimension.options) {
            if (selection.optionId == optionId) {
              if (selection.price) {
                this.distinctPrice = selection.price
              } else if (selection.additionalPrice) {
                this.distinctPrice += selection.additionalPrice
              } else {
                // console.debug('No price or additional price has been found:', dimension, selection)
              }
            }
          }
        }
      }
    }
    
    if (this.distinctPrice > 0) {
      this.priceChangedEvent.emit(this.distinctPrice)
    } else {
      this.priceChangedEvent.emit(undefined)
    }
  }

  /**
   * returns the selection as sorted array
   *
   * @param formData
   * @private
   */
  private getSelectionsArray(formData: DimensionSelectionsInterface): number[] {
    if (! this.product) {
      return []
    }

    let r = []

    for (const dimension of this.product.selectableDimensions) {
      if (formData[dimension.dimensionId]) {
        r.push(formData[dimension.dimensionId])
      }
    }

    return r
  }

  allowDimensionSelection(selectedDimension: ProductDimensionInterface): boolean {
    let selectedDimensionId = selectedDimension.dimensionId
    let hasBeenSet: boolean = true

    if(! this.product) {
      return false
    }

    for (const dimension of this.product.selectableDimensions) {
      let dimensionId = dimension.dimensionId
      let optionId = this.dimensionSelections[dimensionId]

      if (selectedDimensionId == dimensionId) {
        if (! hasBeenSet) {
          return false
        }
      }

      if (optionId === 0) hasBeenSet = false
    }

    return true
  }

  allowOptionSelection(selectedDimension: ProductDimensionInterface, idOption: number): boolean {
    let dimSelection = this.allowDimensionSelection(selectedDimension) 
    let optionValue = this.getSelectableOption(selectedDimension, idOption)

    return dimSelection && (optionValue != null)
  }

  getOptionTooltipImage(selectedDimension: ProductDimensionInterface, idOption: number) {
    let option = this.getOption(selectedDimension, idOption)

    if(option != null) {
      return option.tooltipImageSlug
    }

    return undefined
  }

  getImageLink(slug: string) {
    let width : string = "300"
    return this.envService.config.imagesURL + "/" + slug + "-" + width + ".webp"
  }

  isOptionSelected(selectedDimension: ProductDimensionInterface, idOption: number) {
    let option = this.getOption(selectedDimension, idOption)
    if(option == null) {
      return false
    }

    return this.dimensionSelections[selectedDimension.dimensionId] === option.optionId
  }

  hasSelection(id: number) {
    return this.dimensionSelections[id] !== 0
  }

  isFieldValid(id: number): boolean {
    // return false if field is untouched, this will be touched at least when the submit button was pressed
    if (this.form?.controls[id].untouched) {
      return true
    }
    return this.form?.controls[id].valid ? true : false
  }

  // validateAndAddToCart(): void {
  //   if (! this.product) return
  //
  //   this.form?.markAllAsTouched()
  //
  //   if (! this.form?.valid) return
  //
  //   this.cartService.add({
  //     product: this.product,
  //     selectedVariant: this.product.getVariant().variantId,
  //     distinctId: undefined,
  //     qualityId: 1,
  //     price: this.distinctPrice,
  //     dimensionSelections: JSON.parse(JSON.stringify(this.dimensionSelections))
  //   })
  //
  //   this.reset()
  //   this.validating = false
  // }

  reset() {
    let formData: DimensionSelectionsInterface = {}
    for (let [dimensionId] of Object.entries(this.dimensionSelections)) {
      formData[parseInt(dimensionId)] = 0
      this.dimensionSelections[parseInt(dimensionId)] = 0
    }
    this.form?.reset()
  }
}
